// MobileProfileTab.jsx — mobile-only Profile tab that mimics the Flutter app
// layout: square hero image, overlay buttons, centered name/contact, action
// cards, and stacked sections for job preferences, prompts, social links,
// work experience, education and current resume.
import { useState, useEffect, useMemo, useRef } from "react"
import { collection, onSnapshot, doc as fsDoc, setDoc, updateDoc, deleteDoc, deleteField } from "firebase/firestore"
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage"
import toast from "react-hot-toast"
import {
  Edit3, LifeBuoy, Settings, Mail, Phone, Eye, BarChart3,
  ChevronRight, Plus, Briefcase, GraduationCap, FileText, MapPin,
  Clock, Quote, Link as LinkIcon, Trash2,
} from "lucide-react"
import { db, storage } from "../../firebaseConfig"
import AddResumeModal from "../AddResumeModal"
import {
  JobPreferencesModal, PromptEditorModal, SocialLinkModal,
} from "./ProfileTab"
import MobileBottomNav from "./MobileBottomNav"

const FF = '"Plus Jakarta Sans", "Inter", -apple-system, sans-serif'

// Flutter-mobile palette for the profile screen
const BG       = "#FFFFFF"
const SURFACE  = "#FFFFFF"
const SURFACE_ALT = "#F3F2ED"
const CARD_BORDER = "#E6E5DF"
const INK      = "#0F172A"
const INK_SECONDARY = "#64748B"
const INK_LIGHT = "#94A3B8"
const SOFT_HINT = "#F8F7F3"
const CTA_BG   = "#E0F7ED"

const CITY_KEYS  = ["bengaluru", "mumbai", "delhi_ncr", "hyderabad", "pune", "chennai", "anywhere"]
const CITY_NAMES = ["Bengaluru", "Mumbai", "Delhi NCR", "Hyderabad", "Pune", "Chennai", "Anywhere in India"]

const toInt = (v) => {
  if (v == null) return 0
  if (typeof v === "number") return v
  const n = parseInt(String(v).replace(/,/g, ""), 10)
  return Number.isFinite(n) ? n : 0
}
const initialsOf = (name = "") =>
  (name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]?.toUpperCase() || "").join("") || "?"
const prettyCity = (v = "") => {
  if (!v) return ""
  if (v === "Anywhere in India") return v
  const idx = CITY_KEYS.indexOf(v)
  if (idx >= 0) return CITY_NAMES[idx]
  return v[0].toUpperCase() + v.slice(1)
}
const shortLevel = (level = "") => {
  const map = { SENIOR: "Senior", MID: "Mid-level", JUNIOR: "Junior", LEAD: "Lead", PRINCIPAL: "Principal" }
  return map[String(level).toUpperCase()] || level
}
const lakhLabel = (rupees) => {
  const n = toInt(rupees)
  if (n <= 0) return ""
  const lakh = n / 100000
  const rounded = Math.round(lakh * 10) / 10
  return `₹${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)} LPA`
}
const fmtDate = (m = "", y = "") => [m, y].filter(Boolean).join(" ")

// ── Shared tiny bits ───────────────────────────────────────────────────────
function OverlayIconButton({ icon, onClick, ariaLabel }) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        width: 44, height: 44, borderRadius: 999,
        background: "rgba(255,255,255,0.72)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "none",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: INK, cursor: "pointer",
      }}
    >
      {icon}
    </button>
  )
}

function InfoChip({ icon, label }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px",
      background: SOFT_HINT, borderRadius: 999,
      fontSize: 12, color: INK_SECONDARY, fontWeight: 500,
      maxWidth: "100%",
    }}>
      <span style={{ display: "inline-flex", flexShrink: 0, color: INK_LIGHT }}>{icon}</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
    </span>
  )
}

function SectionLabel({ icon, title, trailing, count }) {
  return (
    <div style={{
      padding: "4px 20px 10px",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      {icon && (
        <span style={{
          width: 28, height: 28, borderRadius: 8,
          background: SURFACE_ALT, color: INK,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          {icon}
        </span>
      )}
      <h3 style={{
        margin: 0, flex: 1,
        fontSize: 18, fontWeight: 700, color: INK, letterSpacing: "-0.3px",
      }}>{title}</h3>
      {typeof count === "number" && count > 0 && (
        <span style={{
          padding: "1px 8px", borderRadius: 999, background: SURFACE_ALT,
          color: INK_SECONDARY, fontSize: 11, fontWeight: 700,
        }}>{count}</span>
      )}
      {trailing}
    </div>
  )
}

function ActionCard({ icon, title, subtitle, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, minWidth: 0,
        padding: "14px 14px",
        background: SURFACE,
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: 14,
        cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "flex-start",
        gap: 10,
        textAlign: "left", fontFamily: FF,
      }}
    >
      <span style={{
        width: 32, height: 32, borderRadius: 8,
        background: SURFACE_ALT, color: INK,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</span>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: INK, letterSpacing: "-0.01em" }}>
        {title}
      </div>
      <div style={{ fontSize: 11.5, color: INK_SECONDARY, fontWeight: 500 }}>
        {subtitle}
      </div>
    </button>
  )
}

// ── Hero image ─────────────────────────────────────────────────────────────
function HeroImage({ userData, userId, onOpenSettings }) {
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef(null)
  const name = userData?.name || ""
  const avatar = userData?.profileUrl || ""

  const pickFile = () => fileRef.current?.click()
  const onFile = async (e) => {
    const f = e.target.files?.[0]
    e.target.value = ""
    if (!f || !userId) return
    if (!f.type.startsWith("image/")) { toast.error("choose an image file"); return }
    if (f.size > 5 * 1024 * 1024) { toast.error("image too large (5MB max)"); return }
    setUploading(true)
    try {
      const ext = f.name.split(".").pop()?.toLowerCase() || "jpg"
      const ref = storageRef(storage, `profileImages/${userId}/profile.${ext}`)
      await uploadBytes(ref, f)
      const url = await getDownloadURL(ref)
      await setDoc(fsDoc(db, "users", userId), { profileUrl: url }, { merge: true })
      toast.success("photo updated")
    } catch (err) {
      console.error("Photo upload failed:", err)
      toast.error("couldn't upload photo. try again.")
    } finally { setUploading(false) }
  }

  return (
    <div style={{
      position: "relative",
      width: "100%",
      aspectRatio: "1 / 1",
      background: SOFT_HINT,
      overflow: "hidden",
    }}>
      {avatar ? (
        <img src={avatar} alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(135deg, #F8F7F3, #EDEBE5)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            width: "40%", aspectRatio: "1 / 1", borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(255,255,255,0.96), rgba(243,242,237,0.86))",
            border: `1.5px solid ${CARD_BORDER}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{
              fontSize: "13vw", fontWeight: 700, color: INK_SECONDARY, letterSpacing: "-0.5px",
            }}>{initialsOf(name)}</span>
          </div>
        </div>
      )}

      {uploading && (
        <div style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span className="mprof-spinner" style={{
            width: 32, height: 32, borderRadius: "50%",
            border: "3px solid rgba(255,255,255,0.4)", borderTopColor: "#fff",
          }} />
        </div>
      )}

      <div style={{
        position: "absolute",
        top: "calc(12px + env(safe-area-inset-top))",
        left: 16,
      }}>
        <OverlayIconButton
          icon={<Edit3 size={20} />}
          onClick={pickFile}
          ariaLabel="Change photo"
        />
      </div>
      <div style={{
        position: "absolute",
        top: "calc(12px + env(safe-area-inset-top))",
        right: 16,
        display: "flex", gap: 10,
      }}>
        <OverlayIconButton
          icon={<LifeBuoy size={20} />}
          onClick={() => toast("support coming soon")}
          ariaLabel="Support"
        />
        <OverlayIconButton
          icon={<Settings size={20} />}
          onClick={onOpenSettings}
          ariaLabel="Settings"
        />
      </div>

      <input ref={fileRef} type="file" accept="image/*" onChange={onFile}
        style={{ display: "none" }} />
    </div>
  )
}

// ── Job preferences section ───────────────────────────────────────────────
function JobPreferences({ userData, onEdit }) {
  const level     = userData?.filters?.level || (Array.isArray(userData?.filters?.levels) ? userData.filters.levels[0] : "")
  const yoe       = toInt(userData?.experience)
  const locs      = (userData?.filters?.locations || []).map(prettyCity).filter(Boolean)
  const currCtcN  = toInt(userData?.currSalary)
  const expCtcN   = toInt(userData?.expSalary)
  const notice    = userData?.noticePeriod || ""
  const roleTitle = userData?.currentJobTitle || userData?.currJobTitle || ""
  const skillsStr = userData?.idealJobSkills || ""
  const descStr   = userData?.idealJobDescription || ""

  const yoeText = yoe > 0 ? `${yoe}+ years` : ""
  const yoeCombo = yoeText && level ? `${shortLevel(level)} (${yoeText})` : (yoeText || "")

  const fields = [
    { labelA: "Role Title *", valueA: roleTitle,         labelB: "Experience",    valueB: yoeCombo },
    { labelA: "Location",      valueA: locs.join(", "),   labelB: "Notice Period", valueB: notice },
    { labelA: "Current CTC",   valueA: lakhLabel(currCtcN), labelB: "Expected CTC",  valueB: lakhLabel(expCtcN) },
  ]

  return (
    <section style={{ marginTop: 12 }}>
      <div style={{
        padding: "12px 16px 0 20px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <h3 style={{
          margin: 0, flex: 1,
          fontSize: 20, fontWeight: 700, color: INK, letterSpacing: "-0.3px",
        }}>
          Job preferences
        </h3>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: "#18B76A" }} />
        <span style={{ fontSize: 12, color: INK_SECONDARY, marginRight: 2 }}>Live</span>
        <button
          onClick={onEdit}
          aria-label="Edit preferences"
          style={{
            width: 32, height: 32, borderRadius: 8,
            background: "transparent", border: "none",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: INK_SECONDARY, cursor: "pointer",
          }}
        >
          <Edit3 size={19} />
        </button>
      </div>

      <p style={{
        margin: "4px 0 0", padding: "0 20px",
        fontSize: 13, color: INK_SECONDARY, lineHeight: 1.35,
      }}>
        These help us connect you to the right bosses
      </p>

      <button
        onClick={onEdit}
        style={{
          margin: "16px 20px 20px",
          width: "calc(100% - 40px)",
          padding: "13px 14px",
          background: CTA_BG, border: "none", borderRadius: 14,
          display: "flex", alignItems: "center", gap: 10,
          fontFamily: FF, cursor: "pointer",
        }}
      >
        <span style={{ fontSize: 17 }}>🚀</span>
        <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: 600, color: INK }}>
          Add your matching preferences (+10% boost)
        </span>
        <ChevronRight size={18} color={INK_SECONDARY} />
      </button>

      <div style={{ padding: "0 20px" }}>
        {fields.map((row, i) => (
          <div key={i} style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 16,
            rowGap: 2,
            marginBottom: i < fields.length - 1 ? 18 : 0,
          }}>
            <PrefTile label={row.labelA} value={row.valueA} />
            <PrefTile label={row.labelB} value={row.valueB} />
          </div>
        ))}
      </div>

      <div style={{ padding: "20px 20px 0", display: "flex", flexDirection: "column", gap: 10 }}>
        <MatchingQuestionCard
          title="What kind of skills would your ideal job have?"
          hint="Add the skills you want in your ideal job to improve your search."
          value={skillsStr}
          onClick={onEdit}
        />
        <MatchingQuestionCard
          title="Describe your ideal job role"
          hint="Describe your ideal job role to improve your search."
          value={descStr}
          onClick={onEdit}
        />
      </div>
    </section>
  )
}

function PrefTile({ label, value }) {
  const empty = !value
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontSize: 10.5, fontWeight: 700, color: INK_LIGHT,
        letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4,
      }}>{label}</div>
      <div style={{
        fontSize: 14,
        fontWeight: empty ? 500 : 700,
        color: empty ? INK_LIGHT : INK,
        letterSpacing: "-0.01em",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>{empty ? "—" : value}</div>
    </div>
  )
}

function MatchingQuestionCard({ title, hint, value, onClick }) {
  const filled = !!value
  return (
    <button onClick={onClick} style={{
      width: "100%", padding: "14px 16px",
      background: filled ? SURFACE : SOFT_HINT,
      border: `1px solid ${CARD_BORDER}`,
      borderRadius: 14,
      cursor: "pointer", textAlign: "left", fontFamily: FF,
      display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: INK, lineHeight: 1.3 }}>{title}</div>
      <div style={{
        fontSize: 13, color: filled ? INK : INK_SECONDARY,
        fontWeight: filled ? 500 : 400,
        lineHeight: 1.45,
        display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>{filled ? value : hint}</div>
    </button>
  )
}

// ── Prompts section ────────────────────────────────────────────────────────
function PromptsSection({ userData, onAdd, onEdit }) {
  const prompts = (userData?.prompts || []).filter(p => p?.prompt && p?.answer)
  const canAdd = prompts.length < 3
  const remaining = Math.max(0, 2 - prompts.length)

  return (
    <section style={{ marginTop: 24 }}>
      <SectionLabel
        icon={<Quote size={16} />}
        title="Prompts"
        trailing={canAdd && (
          <button onClick={() => onAdd(prompts.length)} aria-label="Add prompt"
            style={{
              width: 32, height: 32, border: "none", borderRadius: 8,
              background: "transparent", color: INK_SECONDARY, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>
            <Plus size={20} />
          </button>
        )}
      />
      <p style={{ padding: "0 20px 12px", margin: 0, fontSize: 12.5, color: INK_SECONDARY, lineHeight: 1.45 }}>
        These help you stand out and let you express your work &amp; personality.
      </p>

      {remaining > 0 && (
        <button
          onClick={() => onAdd(prompts.length)}
          style={{
            margin: "0 20px 12px",
            width: "calc(100% - 40px)",
            padding: "12px 14px",
            background: SURFACE_ALT, border: "none", borderRadius: 12,
            display: "flex", alignItems: "center", gap: 10,
            fontFamily: FF, cursor: "pointer", textAlign: "left",
          }}
        >
          <span style={{ fontSize: 15 }}>🚀</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>
            Add {remaining} prompt{remaining === 1 ? "" : "s"} (+{remaining === 1 ? 3 : 5}% boost)
          </span>
        </button>
      )}

      <div style={{ padding: "0 20px", display: "flex", flexDirection: "column", gap: 10 }}>
        {prompts.map((p, i) => (
          <button
            key={i} onClick={() => onEdit(i)}
            style={{
              background: SURFACE, border: `1px solid ${CARD_BORDER}`,
              borderRadius: 14, padding: "14px 16px",
              textAlign: "left", fontFamily: FF, cursor: "pointer",
              display: "flex", flexDirection: "column", gap: 6,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: INK_SECONDARY, lineHeight: 1.3 }}>
              {p.prompt}
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: INK, lineHeight: 1.5 }}>
              {p.answer}
            </div>
          </button>
        ))}
      </div>
    </section>
  )
}

// ── Social links section ──────────────────────────────────────────────────
const SOCIAL_TYPES = [
  { k: "linkedin",   label: "LinkedIn" },
  { k: "github",     label: "GitHub" },
  { k: "leetcode",   label: "LeetCode" },
  { k: "codeforces", label: "Codeforces" },
  { k: "portfolio",  label: "Portfolio" },
  { k: "medium",     label: "Medium" },
  { k: "dribbble",   label: "Dribbble" },
  { k: "youtube",    label: "YouTube" },
]

function SocialLinksSection({ userData, onEdit }) {
  const links = userData?.socialLinks || {}
  return (
    <section style={{ marginTop: 24 }}>
      <SectionLabel icon={<LinkIcon size={16} />} title="Your work" />
      <div style={{
        padding: "0 20px",
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 10,
      }}>
        {SOCIAL_TYPES.map(t => {
          const url = links[t.k]
          const filled = !!url
          return (
            <button
              key={t.k}
              onClick={() => onEdit(t.k)}
              style={{
                padding: "12px 14px",
                background: filled ? SURFACE : SOFT_HINT,
                border: `1px solid ${CARD_BORDER}`,
                borderRadius: 12,
                cursor: "pointer", textAlign: "left",
                display: "flex", alignItems: "center", gap: 10,
                fontFamily: FF,
                minWidth: 0,
              }}
            >
              <span style={{
                width: 28, height: 28, borderRadius: 8,
                background: SURFACE_ALT, color: INK,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, fontSize: 11, fontWeight: 800,
              }}>
                {t.label[0]}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.label}
                </div>
                <div style={{
                  fontSize: 11, color: filled ? INK_SECONDARY : INK_LIGHT,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {filled ? "Added" : "Add"}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </section>
  )
}

// ── Work experience / education timelines ────────────────────────────────
function LogoAvatar({ name = "", size = 38 }) {
  const initials = initialsOf(name)
  return (
    <div style={{
      width: size, height: size, borderRadius: 10,
      background: SURFACE_ALT,
      border: `1px solid ${CARD_BORDER}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
      fontSize: size * 0.32, fontWeight: 800, color: INK, letterSpacing: "-0.3px",
    }}>
      {initials}
    </div>
  )
}

function TimelineItem({ logoName, title, subtitle, meta, isLast }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: `0 20px ${isLast ? 0 : 18}px` }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <LogoAvatar name={logoName} />
        {!isLast && (
          <div style={{ flex: 1, width: 1.5, background: CARD_BORDER, marginTop: 6, minHeight: 12 }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: INK }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: INK_SECONDARY, marginTop: 2 }}>{subtitle}</div>}
        {meta && <div style={{ fontSize: 11, color: INK_LIGHT, marginTop: 2 }}>{meta}</div>}
      </div>
    </div>
  )
}

function WorkExperience({ workHistory, years }) {
  if (!workHistory?.length) {
    return (
      <section style={{ marginTop: 24 }}>
        <SectionLabel icon={<Briefcase size={16} />} title="Work experience" />
        <EmptyHint text="We couldn't find any job history in your file." />
      </section>
    )
  }
  return (
    <section style={{ marginTop: 24 }}>
      <SectionLabel
        icon={<Briefcase size={16} />}
        title="Work experience"
        count={workHistory.length}
        trailing={years > 0 && (
          <span style={{
            padding: "3px 10px", background: SURFACE_ALT, borderRadius: 999,
            fontSize: 11, fontWeight: 700, color: INK_SECONDARY,
          }}>
            {years}+ yrs
          </span>
        )}
      />
      {workHistory.map((w, i) => (
        <TimelineItem
          key={i}
          logoName={w.company}
          title={w.role}
          subtitle={w.company}
          meta={[
            [fmtDate(w.startMonth, w.startYear), w.isCurrentJob ? "Present" : fmtDate(w.endMonth, w.endYear)]
              .filter(Boolean).join(" – "),
            w.location,
          ].filter(Boolean).join(" · ")}
          isLast={i === workHistory.length - 1}
        />
      ))}
    </section>
  )
}

function EducationSection({ education }) {
  if (!education?.length) {
    return (
      <section style={{ marginTop: 24 }}>
        <SectionLabel icon={<GraduationCap size={16} />} title="Education" />
        <EmptyHint text="We couldn't find any education details in your file." />
      </section>
    )
  }
  return (
    <section style={{ marginTop: 24 }}>
      <SectionLabel icon={<GraduationCap size={16} />} title="Education" count={education.length} />
      {education.map((e, i) => (
        <TimelineItem
          key={i}
          logoName={e.institute}
          title={e.institute}
          subtitle={[e.degree, e.field].filter(Boolean).join(" · ")}
          meta={[fmtDate(e.startMonth, e.startYear), fmtDate(e.endMonth, e.endYear)].filter(Boolean).join(" – ")}
          isLast={i === education.length - 1}
        />
      ))}
    </section>
  )
}

// ── Resume tile ───────────────────────────────────────────────────────────
function ResumeTile({ resume, onUpload, onDelete, canDelete }) {
  const label = resume?.fileName || resume?.name || "No resume uploaded"
  return (
    <section style={{ marginTop: 24 }}>
      <SectionLabel icon={<FileText size={16} />} title="Resume" />
      <div style={{ padding: "0 20px" }}>
        <div style={{
          background: SURFACE, border: `1px solid ${CARD_BORDER}`,
          borderRadius: 14, padding: "14px 16px",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10,
            background: SURFACE_ALT, color: INK,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <FileText size={18} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: INK,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{label}</div>
            <div style={{ fontSize: 11, color: INK_SECONDARY, marginTop: 2 }}>
              {resume ? "Active" : "Upload one to match jobs"}
            </div>
          </div>
          {resume && canDelete && (
            <button
              onClick={onDelete}
              aria-label="Delete resume"
              style={{
                width: 36, height: 36, borderRadius: 999, border: `1px solid ${CARD_BORDER}`,
                background: SURFACE, color: "#B42318",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, cursor: "pointer", fontFamily: FF,
              }}
            >
              <Trash2 size={16} />
            </button>
          )}
          <button
            onClick={onUpload}
            style={{
              padding: "8px 14px", borderRadius: 999, border: "none",
              background: INK, color: "#fff",
              fontSize: 12, fontWeight: 700, fontFamily: FF, cursor: "pointer",
            }}
          >
            {resume ? "Change" : "Upload"}
          </button>
        </div>
      </div>
    </section>
  )
}

function EmptyHint({ text }) {
  return (
    <div style={{
      margin: "0 20px",
      padding: "14px 16px",
      background: SOFT_HINT, borderRadius: 12,
      fontSize: 13, color: INK_SECONDARY, lineHeight: 1.5,
    }}>
      {text}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
export default function MobileProfileTab({
  userId, userData, onTabChange, activeTab, pendingApplications,
  onOpenSettings,
}) {
  const [prefsOpen, setPrefsOpen]   = useState(false)
  const [promptIdx, setPromptIdx]   = useState(null)
  const [linkType, setLinkType]     = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [resumes, setResumes]       = useState([])

  const activeResumeId = userData?.filters?.resumeId || userData?.resume?.name || ""
  useEffect(() => {
    if (!userId) return
    const unsub = onSnapshot(collection(db, "users", userId, "resumes"), snap => {
      const list = snap.docs.map(d => ({ name: d.id, ...d.data(), id: d.id }))
      list.sort((a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0))
      setResumes(list)
    })
    return () => unsub()
  }, [userId])

  const activeResume = useMemo(
    () => resumes.find(r => r.id === activeResumeId) || resumes[0] || null,
    [resumes, activeResumeId]
  )

  const workHistory = useMemo(() => (activeResume?.work_history || []).map(w => ({
    company: w.companyName || "", role: w.role || "",
    startMonth: w.startMonth || "", startYear: w.startYear || "",
    endMonth: w.endMonth || "", endYear: w.endYear || "",
    isCurrentJob: !!w.isCurrentJob,
    location: w.location || w.city || w.jobLocation || "",
  })), [activeResume])

  const education = useMemo(() => (activeResume?.education || []).map(e => ({
    degree: e.degree || "", field: e.field || "", institute: e.institute || "",
    startMonth: e.startMonth || "", startYear: e.startYear || "",
    endMonth: e.endMonth || "", endYear: e.endYear || "",
  })), [activeResume])

  const prompts = userData?.prompts || []

  const deleteResume = async () => {
    if (!userId || !activeResume?.id) return
    if (resumes.length <= 1) {
      toast.error("upload another resume before deleting this one")
      return
    }
    if (!window.confirm(`Delete "${activeResume.fileName || activeResume.name}"?`)) return
    try {
      await deleteDoc(fsDoc(db, "users", userId, "resumes", activeResume.id))
      const userPatch = {}
      if (userData?.filters?.resumeId === activeResume.id) userPatch["filters.resumeId"] = deleteField()
      if (userData?.resume?.name === activeResume.id)      userPatch.resume = deleteField()
      if (Object.keys(userPatch).length) {
        await updateDoc(fsDoc(db, "users", userId), userPatch)
      }
      toast.success("resume deleted")
    } catch (err) {
      console.error("Resume delete failed:", err)
      toast.error("couldn't delete resume. try again.")
    }
  }

  const savePrompt = async (idx, value) => {
    if (!userId) return
    const existing = (userData?.prompts || []).filter(p => p?.prompt && p?.answer)
    const next = [...existing]
    if (idx >= next.length) next.push(value)
    else next[idx] = value
    await updateDoc(fsDoc(db, "users", userId), { prompts: next })
  }

  const name  = userData?.name || "Add your name"
  const email = userData?.email || ""
  const phone = userData?.phone || userData?.additionalInfo?.phone || ""

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: BG,
      zIndex: 150,
      fontFamily: FF,
      color: INK,
      overflow: "hidden",
      display: "flex", flexDirection: "column",
    }}>
      <style>{`
        @keyframes mprof-spin { to { transform: rotate(360deg); } }
        .mprof-spinner { animation: mprof-spin 0.75s linear infinite; display: inline-block; }
      `}</style>

      <div style={{
        flex: 1,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: 108,
      }}>
        {/* Hero */}
        <HeroImage
          userData={userData}
          userId={userId}
          onOpenSettings={() => onOpenSettings?.()}
        />

        {/* Rounded white sheet below the hero */}
        <div style={{
          background: BG,
          marginTop: -20,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          paddingTop: 20,
          position: "relative",
        }}>
          {/* Name + chips */}
          <div style={{ padding: "0 20px 4px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: INK, letterSpacing: "-0.3px", textAlign: "center" }}>
              {name}
            </h2>
            {(email || phone) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                {email && <InfoChip icon={<Mail size={13} />} label={email} />}
                {phone && <InfoChip icon={<Phone size={13} />} label={phone} />}
              </div>
            )}
          </div>

          {/* Action cards */}
          <div style={{
            padding: "16px 20px 8px",
            display: "flex", gap: 12,
          }}>
            <ActionCard
              icon={<Eye size={16} />}
              title="View as a boss"
              subtitle="Preview your profile"
              onClick={() => toast("preview — coming soon")}
            />
            <ActionCard
              icon={<BarChart3 size={16} />}
              title="Profile Analytics"
              subtitle="View your insights"
              onClick={() => toast("analytics — coming soon")}
            />
          </div>

          {/* Sections */}
          <JobPreferences userData={userData} onEdit={() => setPrefsOpen(true)} />
          <PromptsSection
            userData={userData}
            onAdd={(len) => setPromptIdx(len)}
            onEdit={(i) => setPromptIdx(i)}
          />
          <SocialLinksSection userData={userData} onEdit={setLinkType} />
          <WorkExperience workHistory={workHistory} years={toInt(userData?.experience)} />
          <EducationSection education={education} />
          <ResumeTile
            resume={activeResume}
            onUpload={() => setUploadOpen(true)}
            onDelete={deleteResume}
            canDelete={resumes.length > 1}
          />

          <div style={{ height: 24 }} />
        </div>
      </div>

      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        pendingApplications={pendingApplications || 0}
      />

      {prefsOpen && (
        <JobPreferencesModal
          userData={userData}
          userId={userId}
          onClose={() => setPrefsOpen(false)}
        />
      )}
      {promptIdx !== null && (
        <PromptEditorModal
          initial={prompts.filter(p => p?.prompt && p?.answer)[promptIdx]}
          usedPrompts={prompts.filter(p => p?.prompt && p?.answer).map(p => p.prompt)}
          onSave={async (val) => { await savePrompt(promptIdx, val); setPromptIdx(null) }}
          onClose={() => setPromptIdx(null)}
        />
      )}
      {linkType && (
        <SocialLinkModal
          type={linkType}
          userData={userData}
          userId={userId}
          onClose={() => setLinkType(null)}
        />
      )}
      {uploadOpen && (
        <AddResumeModal
          userId={userId}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => setUploadOpen(false)}
        />
      )}
    </div>
  )
}
